/* ============================================================================
   Undo journal — every project mutation goes through pmStore.patchProject, so
   that one choke point can keep the previous state of the project it touched.
   Reverting simply writes the remembered version back.

   Deliberately in-memory and session-scoped: an undo stack is a working tool,
   not a record. The permanent record is the synced activity feed, which the
   Audit page reads alongside this.
   ========================================================================== */
import { useEffect, useState } from "react";
import type { Project } from "./types";

export interface UndoEntry {
  id: string;
  pid: string;
  projectName: string;
  /** What changed — taken from the activity the action logged, where it logged one. */
  label: string;
  at: number;
  /** The project exactly as it stood before the change. */
  before: Project;
  /** Set once this entry has been rolled back, so it can't be applied twice. */
  reverted?: boolean;
}

const LIMIT = 25;
let stack: UndoEntry[] = [];
const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => f());

/** Suppressed while a revert is itself being written, so undo never records undo. */
let muted = false;

let seq = 0;

/** Called by patchProject with the before/after pair. Works out a label by
    diffing the activity feed — most actions log one, and that sentence is a
    better description than anything we could invent here. */
export function recordChange(before: Project, after: Project) {
  if (muted) return;
  const newest = after.activity?.[0];
  const wasNewest = before.activity?.[0];
  const label = newest && newest.id !== wasNewest?.id
    ? `${newest.who} ${newest.what}`
    : "Change";
  stack = [{ id: `u${++seq}`, pid: before.id, projectName: before.name, label, at: Date.now(), before }, ...stack].slice(0, LIMIT);
  notify();
}

export function getUndoStack() { return stack; }

/** The most recent change that hasn't been rolled back. */
export function lastUndoable(pid?: string): UndoEntry | undefined {
  return stack.find((e) => !e.reverted && (!pid || e.pid === pid));
}

export function clearUndo() { stack = []; notify(); }

/** Roll the project back to the state remembered in this entry. The write goes
    through the normal store path so it syncs like any other change — it is a
    new change that happens to restore an old shape, not a rewriting of history. */
export function revert(entry: UndoEntry, apply: (pid: string, project: Project) => void) {
  if (entry.reverted) return;
  muted = true;
  try { apply(entry.pid, entry.before); } finally { muted = false; }
  // everything at or above this point in the stack is now meaningless
  const i = stack.indexOf(entry);
  stack = stack.map((e, j) => (j <= i ? { ...e, reverted: true } : e));
  notify();
}

export function useUndoStack(): UndoEntry[] {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return stack;
}
